import moment from "moment";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { getFromServerTopic } from "@utility/slice";
import { IDLE, LOADING } from "@constants";
import {
  calculatePercentChangeForDailyMetricsBins,
  getMonthlyBins,
  mergeDailyMetricsBinsByMonth,
} from "@experiences/dashboard/peakAdvisor/utils";
import { logErrorFilter } from "@logging";
import { getMomentAtStartOfDay } from "@helpers/time";
import { ELECTRIC, STEAM } from "@src/units";
import {
  NO_ACCOUNTS_MESSAGE,
  NOT_ENOUGH_DATA_AVAILABLE_MESSAGE,
} from "@experiences/dashboard/peakAdvisor/constants";

const ACCOUNTS = "accounts";
const DATAPOINT_DATA_RANGE = "datapointDataRange";
const DAILY_TOU_METRICS = "dailyTouMetrics";
const DATAPOINT_INTERVALS = "datapointIntervals";

const SLICE = "peakAdvisor";
const action = (name) => `${SLICE}/${name}`;

const today = () => getMomentAtStartOfDay();

const emptyDateRange = {
  startDate: null,
  endDate: null,
};

const buildingInitialState = {
  accounts: [],
  availableYears: [],
  comparisonYear: null,
  comparisonYearDateRange: {
    startDate: null,
    endDate: null,
  },
  currentYearDateRange: {
    startDate: null,
    endDate: null,
  },
  data: [],
  error: null,
  selectedAccount: null,
  status: LOADING,
};

// --------------------------- GET FROM SERVER ----------------------------- //
const getAccounts = getFromServerTopic({
  topic: ACCOUNTS,
  action: "getAccountsBySiteAndUnitIn",
  contextOptionsFn: ({ arg }) => arg,
  payloadFn: (arg) => arg,
});

const getAccountAndTotalizerByAccountId = getFromServerTopic({
  topic: ACCOUNTS,
  action: "getAccountAndTotalizerByAccountId",
  contextOptionsFn: async ({ arg }) => arg,
  payloadFn: async (arg) => arg,
});

const getAvailableYearsForDatapoint = getFromServerTopic({
  topic: DATAPOINT_DATA_RANGE,
  action: "getDataRangeByDatapointId",
  contextOptionsFn: async ({ arg }) => arg,
  payloadFn: async (arg) => arg,
});

export const getIntervalsByDatapointIdAndDay = getFromServerTopic({
  topic: DATAPOINT_INTERVALS,
  action: "getIntervalsByDatapointIdAndDay",
  contextOptionsFn: async ({ arg }) => arg,
  payloadFn: async (arg) => {
    return arg;
  },
});

const getOnPeakDemandInfoByDatapointIdAndBinMonths = getFromServerTopic({
  topic: DAILY_TOU_METRICS,
  action: "getOnPeakDemandInfoByDatapointIdAndBinMonths",
  contextOptionsFn: async ({ arg }) => arg,
  payloadFn: (arg) => arg,
});

const performAccountUpdate = getFromServerTopic({
  topic: ACCOUNTS,
  action: "updateAccount",
  contextOptionsFn: async ({ arg }) => arg,
  payloadFn: async (arg) => arg,
});

// -------------------------------- HELPERS -------------------------------- //
const setComparisonYear = createAsyncThunk(
  action("setComparisonYear"),
  async (payload) => payload,
);

const setComparisonYearDateRange = (
  state,
  { energyType, siteId, comparisonYear },
) => {
  const { selectedAccount } = state.buildingData[energyType][siteId];
  const startDate = moment(today())
    .month("January")
    .year(comparisonYear)
    .date(selectedAccount?.billDate ?? 1);
  const endDate = moment(startDate).add(12, "month");

  state.buildingData[energyType][siteId]["comparisonYearDateRange"]["endDate"] =
    endDate.toDate();
  state.buildingData[energyType][siteId]["comparisonYearDateRange"][
    "startDate"
  ] = startDate.toDate();
};

const setCurrentYearDateRange = (state, { energyType, siteId }) => {
  const { selectedAccount } = state.buildingData[energyType][siteId];
  const endDate = moment(today()).date(selectedAccount?.billDate ?? 1);
  const startDate = moment(endDate).month("January");

  state.buildingData[energyType][siteId]["currentYearDateRange"]["endDate"] =
    endDate.toDate();
  state.buildingData[energyType][siteId]["currentYearDateRange"]["startDate"] =
    startDate.toDate();
};

// -------------------------------- ACTIONS -------------------------------- //
const getPeakDemandData = createAsyncThunk(
  action("getPeakDemandData"),
  async ({ energyType, siteId }, { dispatch, getState }) => {
    const {
      comparisonYear,
      comparisonYearDateRange,
      currentYearDateRange,
      selectedAccount,
    } = getState().peakAdvisor.buildingData[energyType][siteId];

    const comparisonYearBins = getMonthlyBins({
      startDate: comparisonYearDateRange.startDate,
      endDate: comparisonYearDateRange.endDate,
      currentDate: today().toDate(),
      bucketMethod: selectedAccount?.billBucketMethod ?? "Leading",
    });

    const currentYearBins = getMonthlyBins({
      startDate: currentYearDateRange.startDate,
      endDate: currentYearDateRange.endDate,
      currentDate: today().toDate(),
      bucketMethod: selectedAccount?.billBucketMethod ?? "Leading",
      shouldAppend: true,
    });

    const bins = [...comparisonYearBins, ...currentYearBins];

    await dispatch(
      getOnPeakDemandInfoByDatapointIdAndBinMonths({
        bins,
        comparisonYear,
        currentYear: today().year(),
        datapointId: selectedAccount.datapointId,
        energyType,
        siteId,
      }),
    );
  },
);

const setComparisonDateRanges = createAsyncThunk(
  action("setComparisonDateRanges"),
  async ({ energyType, siteId }, { getState }) => {
    const { comparisonYear } =
      getState().peakAdvisor.buildingData[energyType][siteId];
    return { comparisonYear, energyType, siteId };
  },
);

const setSelectedAccount = createAsyncThunk(
  action("setSelectedAccount"),
  async ({ account, energyType, siteId }, { dispatch }) => {
    await dispatch(
      getAvailableYearsForDatapoint({
        datapointId: account.datapointId,
        energyType,
        siteId,
      }),
    );
    return { account, energyType, siteId };
  },
);

const preparePeakHistory = createAsyncThunk(
  action("preparePeakHistory"),
  async ({ comparisonYear, energyType, siteId }, { dispatch, getState }) => {
    const { availableYears } =
      getState().peakAdvisor.buildingData[energyType][siteId];

    if (availableYears.length > 0) {
      // Cannot sort availableYears directly since it would mutate state, not allowed!
      const length = availableYears.length;
      const sortedYears = [...availableYears].sort((a, b) => b - a);
      await dispatch(
        setComparisonYear({
          comparisonYear: comparisonYear
            ? comparisonYear
            : length === 0
              ? null
              : sortedYears[0],
          energyType,
          siteId,
        }),
      );
      await dispatch(
        setComparisonDateRanges({
          energyType,
          siteId,
        }),
      );
      await dispatch(getPeakDemandData({ energyType, siteId }));
    }
  },
);

const updateAccount = createAsyncThunk(
  action("updateAccount"),
  async ({ account, energyType, siteId }, { dispatch }) => {
    const { accountId } = account;
    await dispatch(performAccountUpdate(account));

    const updatedAccountResponse = await dispatch(
      getAccountAndTotalizerByAccountId({
        accountId,
        energyType,
        siteId,
      }),
    );
    const updatedAccount = updatedAccountResponse.payload.response;

    await dispatch(
      setSelectedAccount({ account: updatedAccount, energyType, siteId }),
    );
  },
);

export const configure = createAsyncThunk(
  action("configure"),
  async (
    { account, comparisonYear, energyType, isAccountUpdated, siteId },
    { dispatch, getState },
  ) => {
    // Result of this conditional block sets availableYears as a side effect
    if (isAccountUpdated) {
      await dispatch(updateAccount({ account, energyType, siteId }));
    } else {
      await dispatch(setSelectedAccount({ account, energyType, siteId }));
    }

    const { selectedAccount } =
      getState().peakAdvisor.buildingData[energyType][siteId];

    await dispatch(preparePeakHistory({ comparisonYear, energyType, siteId }));

    await dispatch(
      getIntervalsByDatapointIdAndDay({
        datapointId: selectedAccount.datapointId,
        day: today().format("YYYY-MM-DD HH:mm:ss"),
        energyType,
        siteId,
      }),
    );
  },
);

export const initialize = createAsyncThunk(
  action("initialize"),
  async ({ energyType, siteId, selectedAccountId }, { dispatch, getState }) => {
    await dispatch(
      getAccounts({
        energyType,
        siteId,
        unitIds: [energyType === "electricity" ? ELECTRIC : STEAM],
      }),
    );

    const { accounts } =
      getState().peakAdvisor.buildingData[energyType][siteId];

    const defaultSelectedAccount = accounts.length
      ? accounts.find((account) => {
          return selectedAccountId
            ? account.accountId === selectedAccountId
            : account.unitId === ELECTRIC;
        })
      : undefined;

    if (!!defaultSelectedAccount) {
      await dispatch(
        configure({
          account: defaultSelectedAccount,
          comparisonYear: null,
          isAccountUpdated: false,
          energyType,
          siteId,
        }),
      );
    } else {
      throw new Error(NO_ACCOUNTS_MESSAGE);
    }
  },
);

// --------------------------------- SLICE --------------------------------- //
export const slice = createSlice({
  name: SLICE,
  initialState: {
    buildingData: {
      electricity: {},
      steam: {},
    },
  },
  reducers: {
    reset: (state, action) => {
      const { energyType, siteId } = action.payload;

      state.buildingData[energyType][siteId] = { ...buildingInitialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(configure.fulfilled, (state, action) => {
        const { energyType, siteId } = action.meta.arg;

        state.buildingData[energyType][siteId].status = IDLE;
      })
      .addCase(getAccounts.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const { energyType, siteId } = meta.arg;
        const { response } = payload;

        state.buildingData[energyType][siteId].accounts = response;
      })
      .addCase(getAvailableYearsForDatapoint.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const { energyType, siteId } = meta.arg;
        const { response } = payload;

        const availableYears = [];
        const rangeStart = moment(
          response.realTimeStartSystem ?? response.realTimeStartManual,
        );
        const rangeEnd = getMomentAtStartOfDay(response.realTimeEnd)
          .month("January")
          .date(1);

        if (
          rangeStart.isValid() &&
          rangeEnd.isValid() &&
          rangeStart.isBefore(rangeEnd)
        ) {
          for (
            const start = moment(rangeStart);
            start.isBefore(rangeEnd);
            start.add(1, "year")
          ) {
            availableYears.push(start.year());
          }

          state.buildingData[energyType][siteId].availableYears = [
            ...availableYears,
          ];
        } else {
          state.buildingData[energyType][siteId].error = new Error(
            NOT_ENOUGH_DATA_AVAILABLE_MESSAGE,
          );
        }
      })
      .addCase(getAvailableYearsForDatapoint.rejected, (state, action) => {
        const { error } = action;
        const { energyType, siteId } = action.meta.arg;

        state.buildingData[energyType][siteId].error = error;
      })
      .addCase(getIntervalsByDatapointIdAndDay.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const { energyType, siteId } = meta.arg;
        const { response } = payload;

        state.buildingData[energyType][siteId].selectedAccount.intervals =
          response;
      })
      .addCase(
        getOnPeakDemandInfoByDatapointIdAndBinMonths.fulfilled,
        (state, action) => {
          const { meta, payload } = action;
          const { comparisonYear, currentYear, energyType, siteId } = meta.arg;
          const { response: bins } = payload;

          const data = calculatePercentChangeForDailyMetricsBins({
            bins,
            comparisonYear,
            currentYear,
          });

          state.buildingData[energyType][siteId].data =
            mergeDailyMetricsBinsByMonth(data);
        },
      )
      .addCase(initialize.pending, (state, action) => {
        const { energyType, siteId } = action.meta.arg;

        let forBuilding = state.buildingData[energyType][siteId];

        if (!forBuilding) {
          forBuilding = { ...buildingInitialState };
          state.buildingData[energyType][siteId] = forBuilding;
        }

        state.buildingData[energyType][siteId].status = LOADING;
      })
      .addCase(initialize.fulfilled, (state, action) => {
        const { energyType, siteId } = action.meta.arg;

        state.buildingData[energyType][siteId].status = IDLE;
      })
      .addCase(initialize.rejected, (state, action) => {
        const { error } = action;
        const { energyType, siteId } = action.meta.arg;

        state.buildingData[energyType][siteId].error = error;
      })
      .addCase(performAccountUpdate.fulfilled, (state, action) => {
        if (!action.payload?.result > 0) {
          logErrorFilter(
            "peakAdvisorSlice: updateAccount -> failed to update account",
            action,
          );
        }
      })
      .addCase(setComparisonDateRanges.fulfilled, (state, action) => {
        const { comparisonYear, energyType, siteId } = action.payload;

        setCurrentYearDateRange(state, {
          energyType,
          siteId,
        });

        if (!!comparisonYear) {
          setComparisonYearDateRange(state, {
            energyType,
            siteId,
            comparisonYear,
          });
        } else {
          state.buildingData[energyType][siteId].comparisonYearDateRange = {
            ...emptyDateRange,
          };
        }
      })
      .addCase(setComparisonYear.fulfilled, (state, action) => {
        const { comparisonYear, energyType, siteId } = action.payload;

        state.buildingData[energyType][siteId].comparisonYear = comparisonYear;
      })
      .addCase(setSelectedAccount.fulfilled, (state, action) => {
        const { account, energyType, siteId } = action.payload;

        state.buildingData[energyType][siteId].selectedAccount = account;
      });
  },
});

export const { reset } = slice.actions;

export default slice.reducer;
